import React, { useEffect, useState } from "react";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import { Typography } from "@material-ui/core";
import {
  FormControl as MuiFormControl,
  InputLabel as MuiInputLabel,
  MenuItem as MuiMenuItem,
  Select as MuiSelect,
  Stack,
} from "@mui/material";
import { Braces, Webhook } from "lucide-react";

import ChatiaDialog from "../ChatiaDialog";

const WEBHOOK_PLATFORMS = [
  { value: "braip", label: "Braip", flag: "/imagens/flag-br.svg" },
];

const WEBHOOK_EVENTS = {
  braip: [
    { value: "cart_abandonment", label: "Abandono de carrinho" },
    { value: "pix_generated", label: "PIX gerado (sem pagamento)" },
    { value: "purchase_approved_new", label: "Pagamento aprovado (novo cliente)" },
    { value: "purchase_approved_existing", label: "Pagamento aprovado (cliente existente)" },
    { value: "payment_overdue", label: "Pagamento atrasado" },
    { value: "account_deactivated", label: "Acesso desativado" },
  ],
};

const AVAILABLE_VARS = {
  cart_abandonment: ["{{nome}}", "{{nomeCompleto}}", "{{produto}}", "{{link}}"],
  pix_generated: ["{{nome}}", "{{nomeCompleto}}", "{{produto}}"],
  purchase_approved_new: ["{{nome}}", "{{nomeCompleto}}", "{{email}}", "{{senha}}", "{{link}}"],
  purchase_approved_existing: ["{{nome}}", "{{nomeCompleto}}", "{{email}}", "{{link}}"],
  payment_overdue: ["{{nome}}", "{{nomeCompleto}}", "{{produto}}"],
  account_deactivated: ["{{nome}}", "{{nomeCompleto}}", "{{produto}}"],
};

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexWrap: "wrap",
  },
  content: {
    gap: 12,
    display: "flex",
    flexDirection: "column",
  },
  infoCard: {
    background: theme.mode === "dark" ? "#16191f" : "#ffffff",
    color: theme.mode === "dark" ? "#e9edef" : "#111b21",
    border: theme.mode === "dark" ? "1px solid rgba(255, 255, 255, 0.08)" : "1px solid rgba(15, 23, 42, 0.10)",
    borderRadius: 12,
    padding: 16,
    boxShadow: "0 10px 28px rgba(0, 0, 0, 0.16)",
  },
  cardHeader: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    marginBottom: 14,
    color: theme.mode === "dark" ? "#e9edef" : "#111b21",
    fontSize: 13,
    fontWeight: 700,
    letterSpacing: 0.1,
  },
  cardIcon: {
    width: 32,
    height: 32,
    borderRadius: 10,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    background: theme.mode === "dark" ? "rgba(255, 255, 255, 0.06)" : "rgba(15, 23, 42, 0.04)",
    border: theme.mode === "dark" ? "1px solid rgba(255, 255, 255, 0.08)" : "1px solid rgba(15, 23, 42, 0.10)",
    color: theme.palette.primary.main,
    "& svg": {
      color: theme.palette.primary.main,
      stroke: theme.palette.primary.main,
    },
  },
  optionRow: {
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  flag: {
    width: 20,
    height: 14,
    borderRadius: 2,
    objectFit: "cover",
  },
  varsCard: {
    background: theme.mode === "dark" ? "rgba(255,255,255,0.06)" : "rgba(15,23,42,0.04)",
    border: theme.mode === "dark" ? "1px solid rgba(255,255,255,0.08)" : "1px solid rgba(15,23,42,0.10)",
    borderRadius: 10,
    padding: 12,
  },
  varsTitle: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    color: theme.mode === "dark" ? "#e9edef" : "#111b21",
    fontSize: 13,
    fontWeight: 700,
    marginBottom: 8,
  },
  varsList: {
    display: "flex",
    flexWrap: "wrap",
    gap: 6,
  },
  varChip: {
    backgroundColor: theme.mode === "dark" ? "rgba(255,255,255,0.06)" : "rgba(15,23,42,0.04)",
    border: theme.mode === "dark" ? "1px solid rgba(255,255,255,0.10)" : "1px solid rgba(15,23,42,0.10)",
    borderRadius: 8,
    color: theme.mode === "dark" ? "#d1d5db" : "#4b5563",
    fontFamily: "monospace",
    fontSize: 12,
    padding: "3px 9px",
  },
  helper: {
    color: theme.mode === "dark" ? "#b8c1cc" : "#667781",
    fontSize: 12,
    marginTop: 8,
  },
}));

const FlowBuilderWebhookModal = ({ open, onSave, onUpdate, data, close }) => {
  const classes = useStyles();
  const theme = useTheme();
  const [activeModal, setActiveModal] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [webhookPlatform, setWebhookPlatform] = useState("");
  const [webhookEvent, setWebhookEvent] = useState("");

  useEffect(() => {
    if (open === "edit" && data) {
      setWebhookPlatform(data.data?.webhookPlatform || "");
      setWebhookEvent(data.data?.webhookEvent || "");
      setIsEdit(true);
      setActiveModal(true);
    } else if (open === "create") {
      setWebhookPlatform("");
      setWebhookEvent("");
      setIsEdit(false);
      setActiveModal(true);
    } else {
      setActiveModal(false);
    }
  }, [open, data]);

  const handleClose = () => {
    setActiveModal(false);
    close(null);
  };

  const handleSave = () => {
    if (isEdit && onUpdate && data) {
      onUpdate({
        ...data,
        data: {
          ...data.data,
          webhookPlatform,
          webhookEvent,
        },
      });
    } else if (onSave) {
      onSave({ webhookPlatform, webhookEvent });
    }
    handleClose();
  };

  const events = webhookPlatform ? (WEBHOOK_EVENTS[webhookPlatform] || []) : [];
  const vars = webhookEvent ? (AVAILABLE_VARS[webhookEvent] || []) : [];

  const selectFieldSx = {
    mt: "8px",
    "& .MuiOutlinedInput-root": {
      borderRadius: "12px !important",
      minHeight: 40,
      color: theme.mode === "dark" ? "#e9edef" : "#111b21",
    },
    "& .MuiOutlinedInput-notchedOutline": {
      borderRadius: "12px !important",
      borderColor: theme.mode === "dark" ? "rgba(255, 255, 255, 0.18)" : "rgba(15, 23, 42, 0.18)",
    },
    "& .MuiInputBase-root": {
      borderRadius: "12px !important",
    },
    "& .MuiInputLabel-root": {
      backgroundColor: theme.mode === "dark" ? "#16191f" : "#ffffff",
      color: theme.mode === "dark" ? "#b8c1cc" : "#667781",
      px: "4px",
    },
    "& .MuiInputLabel-root.Mui-focused": {
      color: theme.mode === "dark" ? "#b8c1cc" : "#667781",
    },
    "& .MuiSvgIcon-root": {
      color: theme.mode === "dark" ? "#b8c1cc" : "#667781",
    },
  };

  const selectMenuProps = {
    PaperProps: {
      sx: {
        mt: 1,
        borderRadius: "14px",
        boxShadow: "0 10px 36px rgba(0,0,0,0.14)",
        border: "none",
        backgroundImage: "none",
        overflow: "hidden",
        "& .MuiMenu-list": {
          padding: "6px",
        },
        "& .MuiMenuItem-root": {
          borderRadius: "8px",
          padding: "9px 12px",
          marginBottom: "4px",
          fontSize: 14,
          fontWeight: 500,
          "&.Mui-selected": {
            background: "rgba(6, 207, 156, 0.10)",
          },
          "&.Mui-selected:hover": {
            background: "rgba(6, 207, 156, 0.16)",
          },
        },
      },
    },
  };

  const CardTitle = ({ icon: Icon, children }) => (
    <div className={classes.cardHeader}>
      <span className={classes.cardIcon}>
        <Icon size={16} />
      </span>
      <span>{children}</span>
    </div>
  );

  return (
    <div className={classes.root}>
      <ChatiaDialog
        open={activeModal}
        onClose={handleClose}
        title={isEdit ? "Editar Webhook" : "Novo Webhook"}
        maxWidth="xs"
        onSave={handleSave}
        saveText={isEdit ? "Salvar" : "Criar"}
        saveDisabled={!webhookPlatform || !webhookEvent}
        contentDividers={false}
      >
        <Stack className={classes.content}>
          <Stack className={classes.infoCard}>
            <CardTitle icon={Webhook}>Configuração do Webhook</CardTitle>

            <MuiFormControl variant="outlined" fullWidth size="small" sx={selectFieldSx}>
              <MuiInputLabel>Integração</MuiInputLabel>
              <MuiSelect
                value={webhookPlatform}
                onChange={(e) => {
                  setWebhookPlatform(e.target.value);
                  setWebhookEvent("");
                }}
                label="Integração"
                MenuProps={selectMenuProps}
              >
                {WEBHOOK_PLATFORMS.map((platform) => (
                  <MuiMenuItem key={platform.value} value={platform.value}>
                    <span className={classes.optionRow}>
                      <img src={platform.flag} alt={platform.label} className={classes.flag} />
                      {platform.label}
                    </span>
                  </MuiMenuItem>
                ))}
              </MuiSelect>
            </MuiFormControl>

            {webhookPlatform && (
              <MuiFormControl variant="outlined" fullWidth size="small" sx={selectFieldSx}>
                <MuiInputLabel>Evento</MuiInputLabel>
                <MuiSelect
                  value={webhookEvent}
                  onChange={(e) => setWebhookEvent(e.target.value)}
                  label="Evento"
                  MenuProps={selectMenuProps}
                >
                  {events.map((event) => (
                    <MuiMenuItem key={event.value} value={event.value}>
                      {event.label}
                    </MuiMenuItem>
                  ))}
                </MuiSelect>
              </MuiFormControl>
            )}

            <Typography className={classes.helper}>
              Selecione a integração e o evento que deve acionar este nó no fluxo.
            </Typography>
          </Stack>

          {vars.length > 0 && (
            <div className={classes.varsCard}>
              <div className={classes.varsTitle}>
                <Braces size={15} />
                Variáveis disponíveis nos nós de mensagem
              </div>
              <div className={classes.varsList}>
                {vars.map((item) => (
                  <span key={item} className={classes.varChip}>
                    {item}
                  </span>
                ))}
              </div>
            </div>
          )}
        </Stack>
      </ChatiaDialog>
    </div>
  );
};

export default FlowBuilderWebhookModal;
